import React, { useState } from 'react';
import { Trophy, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * "Mejores Parciales de Categoría" — collapsible table that lists the
 * absolute session-best (purple) time per sector along with the driver
 * + lap number that set it. Fed by `overallBestSectorsMeta` computed
 * upstream in LiveTelemetryPanel.
 */

const fmtSec = (s) => (s != null && s > 0 ? s.toFixed(3) : '—');

const CategoryBestSectorsTable = ({ overallBestSectorsMeta, sectorCount }) => {
  const [collapsed, setCollapsed] = useState(false);
  const rows = [];
  for (let i = 0; i < sectorCount; i += 1) {
    rows.push({ idx: i, meta: overallBestSectorsMeta?.[i] || null });
  }
  const hasAny = rows.some((r) => r.meta && r.meta.time != null);

  return (
    <div
      data-testid="category-records-panel"
      className="rounded-lg overflow-hidden"
      style={{ backgroundColor: 'var(--dp-surface-0)', border: '1px solid var(--dp-border)' }}
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.02]"
        data-testid="category-records-toggle"
      >
        <Trophy className="w-4 h-4 text-purple-400" />
        <span className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--dp-text-primary)' }}>
          Mejores Parciales · Categoría
        </span>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: 'var(--dp-text-muted)', backgroundColor: 'var(--dp-surface-1)' }}>
          {sectorCount || 0} sectores
        </span>
        <span className="ml-auto text-gray-400">
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </span>
      </button>
      {!collapsed && (
        <div className="px-3 pb-3 pt-1">
          {!hasAny ? (
            <div className="text-xs text-gray-400 py-2">
              Sin datos aún — se llenará a medida que los pilotos cierren su primera vuelta.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="category-records-table">
                <thead>
                  <tr className="text-left" style={{ color: 'var(--dp-text-muted)' }}>
                    <th className="py-1.5 pr-3 font-semibold text-[11px] uppercase tracking-wider">Sector</th>
                    <th className="py-1.5 pr-3 font-semibold text-[11px] uppercase tracking-wider text-right">Tiempo</th>
                    <th className="py-1.5 pr-3 font-semibold text-[11px] uppercase tracking-wider">Piloto</th>
                    <th className="py-1.5 pr-3 font-semibold text-[11px] uppercase tracking-wider text-right">En Vta.</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ idx, meta }) => (
                    <tr
                      key={`rec-${idx}`}
                      className="border-t"
                      style={{ borderColor: 'var(--dp-border)' }}
                      data-testid={`category-record-row-${idx + 1}`}
                    >
                      <td className="py-1.5 pr-3 font-bold text-purple-300">S{idx + 1}</td>
                      <td className="py-1.5 pr-3 text-right tabular-metric font-mono font-bold text-purple-300">
                        {fmtSec(meta?.time)}
                      </td>
                      <td className="py-1.5 pr-3 truncate max-w-[200px]" style={{ color: 'var(--dp-text-primary)' }}>
                        {meta
                          ? <span><span className="text-gray-400 mr-1">#{meta.driver_number}</span>{meta.driver_name}</span>
                          : <span className="text-gray-500">—</span>
                        }
                      </td>
                      <td className="py-1.5 pr-3 text-right text-gray-300">
                        {meta ? meta.lap_number : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryBestSectorsTable;
